import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Globe2,
  Loader2,
  Lock,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiError } from "@/lib/api";
import {
  FOLDER_COLORS,
  FOLDER_ICONS,
  FolderIcon,
  folderColorClasses,
} from "@/components/folderTheme";
import { formatRunNumber } from "@/lib/runNumber";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDuration(ms) {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

export default function FolderDetailPage() {
  const { folder_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const folderQuery = useQuery({
    queryKey: ["folder", folder_id],
    queryFn: async () => (await api.get(`/folders/${folder_id}`)).data,
    retry: false,
  });

  const runsQuery = useQuery({
    queryKey: ["folder-runs", folder_id],
    queryFn: async () =>
      (await api.get(`/folders/${folder_id}/runs?page_size=200`)).data,
    enabled: folderQuery.isSuccess,
  });

  const removeMutation = useMutation({
    mutationFn: async (runId) =>
      api.delete(`/folders/${folder_id}/items/${runId}`),
    onSuccess: () => {
      toast.success("Run removed from folder");
      queryClient.invalidateQueries({ queryKey: ["folder-runs", folder_id] });
      queryClient.invalidateQueries({ queryKey: ["folder", folder_id] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/folders/${folder_id}`),
    onSuccess: () => {
      toast.success("Folder deleted");
      queryClient.invalidateQueries({ queryKey: ["folders-list"] });
      navigate("/folders");
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  if (folderQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-16 text-slate-500">
        <Loader2 className="animate-spin" /> Loading folder…
      </div>
    );
  }
  if (folderQuery.isError) {
    return (
      <div
        className="rounded-xl border border-rose-200 bg-rose-50 p-8 text-rose-800"
        data-testid="folder-detail-error"
      >
        <AlertTriangle className="mb-2" />
        <h1 className="text-lg font-semibold">Folder unavailable</h1>
        <p className="text-sm">
          You may not have permission to view this folder, or it was deleted.
        </p>
        <Link to="/folders" className="mt-3 inline-block text-sm text-indigo-700 hover:underline">
          ← Back to all folders
        </Link>
      </div>
    );
  }

  const folder = folderQuery.data;
  const palette = folderColorClasses(folder.color);
  const runs = runsQuery.data?.items || [];

  return (
    <div className="space-y-6" data-testid="folder-detail-page">
      <div>
        <Link
          to="/folders"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={12} /> All folders
        </Link>
      </div>
      <header className="flex items-start gap-4">
        <div
          className={[
            "grid h-14 w-14 shrink-0 place-items-center rounded-xl",
            palette.bg,
            palette.text,
          ].join(" ")}
        >
          <FolderIcon name={folder.icon} size={28} />
        </div>
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl font-semibold tracking-tight text-slate-900"
            data-testid="folder-detail-name"
          >
            {folder.name}
          </h1>
          {folder.description && (
            <p className="mt-1 text-sm text-slate-500">{folder.description}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              {folder.visibility === "org" ? (
                <>
                  <Globe2 size={11} className="text-emerald-700" /> Org-wide
                </>
              ) : (
                <>
                  <Lock size={11} /> Private
                </>
              )}
            </span>
            <span>·</span>
            <span data-testid="folder-detail-count">
              {folder.item_count} run{folder.item_count === 1 ? "" : "s"}
            </span>
            <span>·</span>
            <span>
              Created {fmtDate(folder.created_at)} by{" "}
              <code className="font-mono">{folder.created_by}</code>
            </span>
          </div>
        </div>
        <div className="flex gap-1">
          {folder.can_edit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
              className="gap-1.5"
              data-testid="folder-edit-button"
            >
              <Pencil size={12} /> Edit
            </Button>
          )}
          {folder.can_delete && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
              data-testid="folder-delete-button"
            >
              <Trash2 size={12} /> Delete
            </Button>
          )}
        </div>
      </header>

      {runsQuery.isLoading ? (
        <div className="py-8 text-center text-sm text-slate-500">
          <Loader2 className="mr-2 inline animate-spin" size={14} /> Loading runs…
        </div>
      ) : runs.length === 0 ? (
        <div
          className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-10 text-center text-slate-500"
          data-testid="folder-runs-empty"
        >
          No runs in this folder yet. From any run detail page, click the
          "Add to folder" button to add it here.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2">Run #</th>
                <th className="px-3 py-2">Workflow</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.map((r) => (
                <tr key={r.run_id} data-testid={`folder-run-row-${r.run_id}`}>
                  <td className="px-3 py-2.5 font-mono text-[11px]">
                    #{formatRunNumber(r.run_number)}
                  </td>
                  <td className="px-3 py-2.5">
                    <Link
                      to={`/runs/${r.run_id}`}
                      className="text-indigo-700 hover:underline"
                    >
                      {r.workflow_name_snapshot}
                    </Link>
                    <div className="text-[10px] text-slate-400 font-mono">{r.run_id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={[
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                        r.status === "completed"
                          ? "bg-emerald-50 text-emerald-700"
                          : r.status === "failed"
                          ? "bg-rose-50 text-rose-700"
                          : r.status === "cancelled"
                          ? "bg-slate-100 text-slate-600"
                          : "bg-amber-50 text-amber-700",
                      ].join(" ")}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[11px]">{fmtDuration(r.duration_ms)}</td>
                  <td className="px-3 py-2.5 text-[11px]">{fmtDate(r.started_at)}</td>
                  <td className="px-3 py-2.5 text-right">
                    {folder.can_modify_items && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 px-2 text-[10px] text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                        onClick={() => removeMutation.mutate(r.run_id)}
                        disabled={removeMutation.isPending}
                        data-testid={`folder-run-remove-${r.run_id}`}
                      >
                        <X size={11} /> Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EditFolderModal
        folder={folder}
        open={editOpen}
        onOpenChange={setEditOpen}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="folder-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this folder?</AlertDialogTitle>
            <AlertDialogDescription>
              The folder will be removed. The {folder.item_count} demo run
              {folder.item_count === 1 ? "" : "s"} inside it stay intact —
              only the collection itself is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => deleteMutation.mutate()}
              data-testid="folder-delete-confirm-button"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditFolderModal({ folder, open, onOpenChange }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(folder.name);
  const [description, setDescription] = useState(folder.description || "");
  const [color, setColor] = useState(folder.color);
  const [icon, setIcon] = useState(folder.icon);
  const [visibility, setVisibility] = useState(folder.visibility);

  React.useEffect(() => {
    if (open) {
      setName(folder.name);
      setDescription(folder.description || "");
      setColor(folder.color);
      setIcon(folder.icon);
      setVisibility(folder.visibility);
    }
  }, [open, folder]);

  const saveMutation = useMutation({
    mutationFn: async () =>
      (
        await api.patch(`/folders/${folder.folder_id}`, {
          name: name.trim(),
          description: description.trim() || null,
          color,
          icon,
          visibility,
        })
      ).data,
    onSuccess: () => {
      toast.success("Folder updated");
      queryClient.invalidateQueries({ queryKey: ["folder", folder.folder_id] });
      queryClient.invalidateQueries({ queryKey: ["folders-list"] });
      onOpenChange(false);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const palette = folderColorClasses(color);
  const nameOk = name.trim().length >= 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="folder-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-base">Edit folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="folder-edit-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Icon</Label>
              <div className="flex flex-wrap gap-1">
                {FOLDER_ICONS.map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    onClick={() => setIcon(opt)}
                    className={[
                      "grid h-8 w-8 place-items-center rounded-md border",
                      icon === opt
                        ? `${palette.border} ${palette.bg} ${palette.text}`
                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <FolderIcon name={opt} size={14} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Colour</Label>
              <div className="flex flex-wrap gap-1">
                {FOLDER_COLORS.map((opt) => {
                  const p = folderColorClasses(opt);
                  return (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => setColor(opt)}
                      title={opt}
                      className={[
                        "h-7 w-7 rounded-md border",
                        p.bg,
                        color === opt ? "ring-2 ring-slate-900 ring-offset-2" : p.border,
                      ].join(" ")}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Visibility</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="org">Org-wide</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!nameOk || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="folder-edit-save"
          >
            {saveMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
